import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { FileText, PenTool, Info, Clock, Check } from 'lucide-react';
import { evaluateFormula, formatComputed, computeTableRows, computeTotalsRow } from '../../lib/formCalc';
import FillBlankField from './FillBlankField';

/**
 * Read-only mirror of FormFillPage's field rendering — used when viewing or
 * printing a submitted form so the layout looks IDENTICAL to the blank form
 * the user originally filled out (same widths, sections, multi-column grids,
 * checkbox tables, etc.) but with inputs replaced by their stored values.
 *
 * Props:
 *   submission — { form_fields: [...], responses: {...}, e_signers?: [...] }
 *   form?      — optional source form (for `referenced_forms` lookup); falls back to submission
 */

// ---- helpers ----------------------------------------------------------------
const v = (responses, fid) => responses?.[fid];

function ValueLine({ value, placeholder = '—', mono = false, className = '' }) {
  const display = value === null || value === undefined || value === '' ? placeholder : String(value);
  return (
    <div className={`min-h-[34px] px-3 py-1.5 rounded-md border border-[#E2E8F0] bg-[#FAFBFC] text-sm text-[#0A2342] ${mono ? 'font-mono' : ''} ${className}`}>
      {display}
    </div>
  );
}

function CheckGlyph({ checked }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 rounded border ${checked ? 'bg-[#0E7490] border-[#0E7490]' : 'border-[#94A3B8] bg-white'} shrink-0`}
      aria-label={checked ? 'checked' : 'unchecked'}
    >
      {checked ? <Check className="w-3 h-3 text-white" strokeWidth={3} /> : null}
    </span>
  );
}

function RadioGlyph({ checked }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${checked ? 'border-[#0E7490]' : 'border-[#94A3B8]'} shrink-0`}
      aria-label={checked ? 'selected' : 'unselected'}
    >
      {checked ? <span className="w-2 h-2 rounded-full bg-[#0E7490]" /> : null}
    </span>
  );
}

// Render a single child field in a multi_column container
function MultiColumnChild({ child, val, childKey }) {
  const cval = val[childKey];
  if (['text', 'number', 'email', 'phone', 'textarea'].includes(child.field_type)) {
    if (child.field_type === 'textarea') {
      return <div className="min-h-[60px] whitespace-pre-wrap px-3 py-1.5 rounded-md border border-[#E2E8F0] bg-[#FAFBFC] text-sm text-[#0A2342]">{cval || '—'}</div>;
    }
    return <ValueLine value={cval} />;
  }
  if (child.field_type === 'date') return <ValueLine value={cval} />;
  if (child.field_type === 'select') return <ValueLine value={cval} />;
  if (child.field_type === 'checkbox') {
    const arr = Array.isArray(cval) ? cval : [];
    return (
      <div className="space-y-1">
        {(child.options || []).map((opt, oi) => (
          <div key={oi} className="flex items-center gap-2">
            <CheckGlyph checked={arr.includes(opt)} />
            <span className="text-sm text-[#0A2342]">{opt}</span>
          </div>
        ))}
      </div>
    );
  }
  if (child.field_type === 'radio') {
    return (
      <div className="space-y-1">
        {(child.options || []).map((opt, oi) => (
          <div key={oi} className="flex items-center gap-2">
            <RadioGlyph checked={cval === opt} />
            <span className="text-sm text-[#0A2342]">{opt}</span>
          </div>
        ))}
      </div>
    );
  }
  if (child.field_type === 'info') {
    return <div className="p-2 bg-[#F0FDFA] rounded text-xs text-[#0A2342]">{child.info_text || child.label}</div>;
  }
  return <ValueLine value={typeof cval === 'object' ? JSON.stringify(cval) : cval} />;
}

// ---- main component --------------------------------------------------------
export default function FormFilledView({ submission, form }) {
  const fields = submission?.form_fields || form?.fields || [];
  const responses = submission?.responses || {};
  const referenced = form?.referenced_forms || submission?.referenced_forms || {};

  return (
    <div className="space-y-1" data-testid="form-filled-view">
      <div className="flex flex-wrap gap-4 pr-2">
        {[...fields].sort((a, b) => (a.order || 0) - (b.order || 0)).map((field, idx) => {
          const widthClass = field.width === 'half'
            ? 'w-full md:w-[calc(50%-8px)]'
            : field.width === 'third'
              ? 'w-full md:w-[calc(33.333%-11px)]'
              : 'w-full';

          // Section divider — full-width, no card around it
          if (field.field_type === 'section') {
            return (
              <div key={field.field_id} className="w-full pt-3 pb-1">
                <h3 className="text-sm font-bold text-[#0E7490] border-b-2 border-[#0E7490] pb-1">{field.label}</h3>
              </div>
            );
          }
          // Reminder — non-essential at print/view
          if (field.field_type === 'reminder') {
            return (
              <div key={field.field_id} className={`${widthClass} p-3 bg-[#FEF3C7]/50 border border-[#CA8A04]/20 rounded-lg text-sm`}>
                <div className="flex items-center gap-2 text-[#92400E]">
                  <Clock className="w-4 h-4" />
                  <span>{field.reminder_label || 'Follow-up'} reminder — {field.reminder_days || 0} day(s) after submission</span>
                </div>
              </div>
            );
          }
          // Info block
          if (field.field_type === 'info') {
            return (
              <div key={field.field_id} className={`${widthClass} p-3 bg-[#F0FDFA] border border-[#14B8A6]/20 rounded-lg text-sm text-[#0A2342]`} data-testid={`view-info-${field.field_id}`}>
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-[#14B8A6] mt-0.5 shrink-0" />
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: field.info_text || field.label }} />
                </div>
              </div>
            );
          }
          // Form-reference field
          if (field.field_type === 'form_reference') {
            return (
              <Card key={field.field_id} className={`careiq-card ${widthClass}`}>
                <CardContent className="p-4">
                  <Label className="font-medium text-[#0A2342]">{field.label}</Label>
                  <div className="mt-2 p-3 bg-[#0E7490]/5 rounded-lg text-sm text-[#0E7490]">
                    <FileText className="w-4 h-4 inline mr-1" />
                    References form: {referenced?.[field.reference_form_id]?.name || field.reference_form_id || '—'}
                  </div>
                </CardContent>
              </Card>
            );
          }

          const fid = field.field_id;
          const value = v(responses, fid);

          return (
            <Card key={fid} className={`careiq-card ${widthClass}`} data-testid={`view-field-${fid}`}>
              <CardContent className="p-4">
                <Label className="flex items-center gap-1 mb-2">
                  {field.label}
                  {field.required && <span className="text-[#DC2626]">*</span>}
                </Label>

                {(field.field_type === 'text' || field.field_type === 'number' || field.field_type === 'email' || field.field_type === 'phone' || field.field_type === 'date') && (
                  <ValueLine value={value} />
                )}

                {field.field_type === 'textarea' && (
                  <div className="min-h-[80px] whitespace-pre-wrap px-3 py-1.5 rounded-md border border-[#E2E8F0] bg-[#FAFBFC] text-sm text-[#0A2342]">
                    {value || '—'}
                  </div>
                )}

                {field.field_type === 'select' && <ValueLine value={value} />}

                {field.field_type === 'radio' && (
                  <div className="space-y-2 mt-1">
                    {(field.options || []).map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <RadioGlyph checked={value === opt} />
                        <span className="text-sm text-[#0A2342]">{opt}</span>
                      </div>
                    ))}
                  </div>
                )}

                {field.field_type === 'checkbox' && (
                  <div className="space-y-2 mt-1">
                    {(field.options || []).map((opt, i) => {
                      const checked = Array.isArray(value) ? value.includes(opt) : false;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <CheckGlyph checked={checked} />
                          <span className="text-sm text-[#0A2342]">{opt}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {field.field_type === 'checkbox_text' && (
                  <div className="space-y-2 mt-1">
                    {(field.options || []).map((opt, i) => {
                      const val = value || {};
                      const chk = !!val[`chk_${i}`];
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <CheckGlyph checked={chk} />
                          <span className="text-sm text-[#0A2342] shrink-0 min-w-[80px]">{opt}</span>
                          <ValueLine value={val[`txt_${i}`]} className="h-8 flex-1" />
                        </div>
                      );
                    })}
                  </div>
                )}

                {field.field_type === 'signature' && (
                  <div className="border-2 border-dashed border-[#94A3B8] rounded-lg p-4 text-center" data-testid={`view-signature-${fid}`}>
                    {value && typeof value === 'string' && value.startsWith('data:image') ? (
                      <>
                        <img src={value} alt={`Signature for ${field.label}`} className="max-h-24 mx-auto border border-[#E2E8F0] rounded bg-white" />
                        {(() => {
                          const signer = (submission?.e_signers || []).find(s =>
                            Array.isArray(s.signature_fields) && s.signature_fields.includes(fid)
                          );
                          return signer ? (
                            <div className="text-[10px] text-[#166534] mt-1">
                              Signed by <b>{signer.signer_name}</b> ({signer.signer_type}) on {new Date(signer.signed_at).toLocaleString()}
                            </div>
                          ) : null;
                        })()}
                      </>
                    ) : value ? (
                      <div className="text-sm text-[#166534] font-medium"><PenTool className="w-4 h-4 inline mr-1" />{value}</div>
                    ) : (
                      <div className="text-sm text-[#94A3B8]"><PenTool className="w-6 h-6 mx-auto mb-1" />Not signed</div>
                    )}
                  </div>
                )}

                {field.field_type === 'calculated' && (
                  <div className="mt-1 px-3 py-2 bg-[#EFF6FF] border border-[#2563EB]/20 rounded-lg font-mono text-base text-[#1E40AF]" data-testid={`view-calc-${fid}`}>
                    {formatComputed(evaluateFormula(field.formula || '', { fields: responses }), field.format || { type: 'number', decimals: 2 })}
                    <span className="text-[10px] text-[#64748B] ml-2">(auto-calculated)</span>
                  </div>
                )}

                {field.field_type === 'calc_table' && (() => {
                  const cfg = field.calc_table_config || { columns: [] };
                  const stored = value || {};
                  const rawRows = Array.isArray(stored.rows) ? stored.rows : [];
                  const computedRows = computeTableRows(cfg, rawRows, { fields: responses });
                  const totalsRow = computeTotalsRow(cfg, computedRows, { fields: responses });
                  const cols = cfg.columns || [];
                  // Default to ZERO decimals — most clinical scoring tables (nutrition, falls,
                  // PHQ-9, etc.) work in whole numbers. Authors can opt back into decimals
                  // by setting `format` on individual columns or the field itself.
                  const defaultFmt = { type: 'number', decimals: 0 };
                  return (
                    <div className="overflow-x-auto border border-[#E2E8F0] rounded-lg" data-testid={`view-calctable-${fid}`}>
                      <table className="w-full text-sm">
                        <thead className="bg-[#F1F5F9]">
                          <tr>
                            {cols.map(c => <th key={c.key} className="p-2 text-left text-xs font-medium text-[#64748B] border-b">{c.label}{c.type === 'formula' && <span className="text-[9px] text-[#94A3B8] ml-1">(auto)</span>}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {computedRows.map((row, ri) => (
                            <tr key={ri} className="border-b border-[#E2E8F0]">
                              {cols.map(c => (
                                <td key={c.key} className="p-2">
                                  {c.type === 'formula' ? (
                                    <span className="text-[#1E40AF] font-mono">{formatComputed(row[c.key], c.format || field.format || { type: 'number', decimals: 0 })}</span>
                                  ) : (
                                    <span className="text-[#0A2342]">{row[c.key] === null || row[c.key] === undefined || row[c.key] === '' ? '—' : String(row[c.key])}</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                          {totalsRow && (
                            <tr className="bg-[#F8FAFC] font-semibold border-t-2 border-[#0E7490]">
                              {cols.map(c => (
                                <td key={c.key} className="p-2 text-sm">
                                  {totalsRow[c.key] !== undefined && totalsRow[c.key] !== null ? (
                                    <span className="text-[#0E7490]">{formatComputed(totalsRow[c.key], c.format || field.format || { type: 'number', decimals: 0 })}</span>
                                  ) : (
                                    c === cols[0] ? <span className="text-[#475569]">Total</span> : ''
                                  )}
                                </td>
                              ))}
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {field.field_type === 'checkbox_table' && (
                  <div className="overflow-x-auto" data-testid={`view-checktable-${fid}`}>
                    <table className="w-full text-sm border border-[#E2E8F0] rounded-lg">
                      <thead><tr className="bg-[#F1F5F9]">
                        {(field.table_columns || ['Item']).map((col, ci) => <th key={ci} className="p-2 text-left text-xs font-medium text-[#64748B] border-b">{col}</th>)}
                      </tr></thead>
                      <tbody>
                        {(field.table_rows || []).map((row, ri) => (
                          <tr key={ri} className="border-b border-[#E2E8F0]">
                            <td className="p-2 text-xs font-medium text-[#0A2342]">{row}</td>
                            {(field.table_columns || []).slice(1).map((col, ci) => {
                              const checked = !!((value || {})[`${ri}_${ci}`]);
                              return (
                                <td key={ci} className="p-2 text-center">
                                  <CheckGlyph checked={checked} />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {field.field_type === 'multi_column' && (
                  <div>
                    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${field.column_count || 2}, 1fr)` }}>
                      {(field.children || []).map((col, colIdx) => (
                        <div key={colIdx} className="space-y-3">
                          {(col || []).map((child, ci) => {
                            const childKey = `${fid}_${colIdx}_${ci}`;
                            return (
                              <div key={ci} className="space-y-1">
                                {child.label && <Label>{child.label}</Label>}
                                <MultiColumnChild child={child} val={value || {}} childKey={childKey} />
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {field.field_type === 'file' && (
                  <ValueLine value={typeof value === 'string' ? value : (value ? 'File attached' : 'No file')} />
                )}

                {field.field_type === 'fill_blank' && (
                  <FillBlankField
                    template={field.fib_template || ''}
                    value={Array.isArray(value) ? value : []}
                    onChange={() => {}}
                    readOnly
                    testIdPrefix={`fib-view-${fid}`}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
